import React, { useEffect, createElement } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Calendar, MessageCircle, Phone } from 'lucide-react';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-475" data-mp="2a5nt6nrcqpa">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-476" data-mp="27t0sf357q5d" />
    </svg>;
}
// Language code mapping for <html lang="">
const htmlLangMap: Record<string, string> = {
  en: 'en',
  ua: 'uk',
  ru: 'ru',
  ro: 'ro'
};
// Default (homepage) meta tags per language — used when a page does not
// supply its own SEO title/description via props.
const metaTags: Record<string, {
  title: string;
  description: string;
}> = {
  en: {
    title: 'Private Dentist in Angel, London | Dentico Clinic',
    description: 'Dentico Clinic is a modern private dental clinic in Angel, London, offering general, cosmetic, orthodontic and emergency dental care.'
  },
  ua: {
    title: 'Український стоматолог у Лондоні (Angel) | Dentico Clinic',
    description: 'Dentico Clinic — українська стоматологія в Angel, London EC1V. Консультація українською мовою. Імпланти, вініри, лікування каналів, гігієна та невідкладна допомога. Запис онлайн / WhatsApp / Telegram.'
  },
  ru: {
    title: 'Стоматолог в Лондоне (Angel) | Dentico Clinic',
    description: 'Dentico Clinic — современная частная стоматология в Angel, London EC1V. Консультация на русском языке. Импланты, виниры, лечение каналов, гигиена. Запись онлайн.'
  },
  ro: {
    title: 'Dentist Privat în Angel, Londra | Dentico Clinic',
    description: 'Dentico Clinic — clinică dentară privată modernă în Angel, Londra EC1V. Stomatologie digitală, prețuri transparente, echipă multilingvă. Programare online.'
  }
};
// The non-www production domain is the single canonical host used across the site.
const BASE_URL = 'https://denticoclinic.co.uk';
const locales = ['en', 'ua', 'ru', 'ro'] as const;
const hreflangMap: Record<(typeof locales)[number], string> = {
  en: 'en',
  ua: 'uk',
  ru: 'ru',
  ro: 'ro'
};
const ogLocaleMap: Record<(typeof locales)[number], string> = {
  en: 'en_GB',
  ua: 'uk_UA',
  ru: 'ru_RU',
  ro: 'ro_RO'
};
function getPathWithoutLocale(pathname: string) {
  const path = pathname.replace(/^\/(en|ua|ru|ro)(?=\/|$)/, '') || '/';
  return path === '/' ? '' : path.replace(/\/$/, '');
}
interface LayoutProps {
  children: React.ReactNode;
  /** Optional per-page SEO title. Falls back to the language default. */
  title?: string;
  /** Optional per-page SEO meta description. Falls back to the language default. */
  description?: string;
}
export function Layout({
  children,
  title,
  description
}: LayoutProps) {
  const {
    lang
  } = useTranslation();
  const {
    pathname
  } = useLocation();
  // 1) Set <html lang="..."> dynamically
  // 2) Set title, description, canonical and language alternates dynamically
  useEffect(() => {
    // Set html lang attribute
    document.documentElement.lang = htmlLangMap[lang] || 'en';
    // Resolve effective meta: per-page props take priority over the
    // language default so each page can have unique SEO metadata.
    const defaults = metaTags[lang] || metaTags.en;
    const meta = {
      title: title || defaults.title,
      description: description || defaults.description
    };
    document.title = meta.title;
    // Set or create meta description
    let descTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.name = 'description';
      document.head.appendChild(descTag);
    }
    descTag.content = meta.description;
    // Set or create og:title
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = meta.title;
    // Set or create og:description
    let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.content = meta.description;
    // Set or create og:locale
    let ogLocale = document.querySelector('meta[property="og:locale"]') as HTMLMetaElement | null;
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      ogLocale.setAttribute('property', 'og:locale');
      document.head.appendChild(ogLocale);
    }
    ogLocale.content = ogLocaleMap[lang] || ogLocaleMap.en;

    // Language-prefixed routes are individually indexable. Strip any existing
    // locale before rebuilding URLs so `/ro/services` never becomes `/ro/ro/services`.
    const pathWithoutLocale = getPathWithoutLocale(pathname);
    const currentUrl = `${BASE_URL}/${lang}${pathWithoutLocale}`;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = currentUrl;
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = currentUrl;
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    locales.forEach(locale => {
      const alternate = document.createElement('link');
      alternate.rel = 'alternate';
      alternate.hreflang = hreflangMap[locale];
      alternate.href = `${BASE_URL}/${locale}${pathWithoutLocale}`;
      document.head.appendChild(alternate);
    });
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = `${BASE_URL}/en${pathWithoutLocale}`;
    document.head.appendChild(xDefault);
    // === NEW: Robots meta ===
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = 'index,follow,max-image-preview:large';
    // === NEW: Content-Language meta ===
    let contentLang = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement | null;
    if (!contentLang) {
      contentLang = document.createElement('meta');
      contentLang.httpEquiv = 'content-language';
      document.head.appendChild(contentLang);
    }
    contentLang.content = htmlLangMap[lang] || 'en';
  }, [lang, pathname, title, description]);
  // 3) Enhanced structured data with full Schema.org markup
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': ['Dentist', 'MedicalBusiness', 'LocalBusiness'],
    '@id': 'https://denticoclinic.co.uk/#denticoclinic',
    name: 'Dentico Clinic',
    url: lang === 'ua' ? 'https://denticoclinic.co.uk/ua' : lang === 'ru' ? 'https://denticoclinic.co.uk/ru' : lang === 'ro' ? 'https://denticoclinic.co.uk/ro' : 'https://denticoclinic.co.uk',
    image: 'https://cdn.magicpatterns.com/uploads/kCd6PjT8cHCFux7c2JDXSh/7800B156-90A9-4047-97F1-B20E4E965E7F.png',
    logo: 'https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png',
    description: lang === 'ua' ? 'Dentico Clinic — сучасна приватна стоматологія в Angel, Central London (EC1V). Консультація українською мовою. Огляди, гігієна, пломби, коронки, вініри, імпланти та невідкладна допомога.' : lang === 'ru' ? 'Dentico Clinic — современная частная стоматология в Angel, Central London (EC1V). Консультация на русском языке. Осмотры, гигиена, пломбы, коронки, виниры, импланты.' : 'Dentico Clinic — modern private dental clinic in Angel, Central London (EC1V). Consultations available in Ukrainian. Check-ups, hygiene, fillings, crowns, veneers, implants & emergency dental care.',
    telephone: '+44 7345 197820',
    email: 'info@denticoclinic.co.uk',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '360–364 City Road',
      addressLocality: 'London',
      postalCode: 'EC1V 2PY',
      addressCountry: 'GB'
    },
    areaServed: [{
      '@type': 'City',
      name: 'London'
    }, {
      '@type': 'AdministrativeArea',
      name: 'Islington'
    }],
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 51.5308911,
      longitude: -0.1029003
    },
    hasMap: 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6',
    priceRange: '££',
    knowsLanguage: ['en', 'uk', 'ru', 'ro'],
    medicalSpecialty: ['Dentistry', 'Cosmetic Dentistry', 'Endodontics', 'Dental Implants', 'Restorative Dentistry'],
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00'
    }, {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '10:00',
      closes: '16:00'
    }],
    contactPoint: [{
      '@type': 'ContactPoint',
      contactType: 'appointments',
      telephone: '+44 7345 197820',
      email: 'info@denticoclinic.co.uk',
      availableLanguage: ['English', 'Ukrainian', 'Russian', 'Romanian']
    }],
    sameAs: ['https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6', 'https://instagram.com/dentico_clinic'],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '30',
      bestRating: '5'
    },
    potentialAction: [{
      '@type': 'ReserveAction',
      name: lang === 'ua' ? 'Записатись на прийом' : lang === 'ru' ? 'Записаться на приём' : 'Book an appointment',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: BOOKING_URL,
        inLanguage: htmlLangMap[lang] || 'en',
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
      }
    }, {
      '@type': 'CommunicateAction',
      name: 'WhatsApp',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: WHATSAPP_URL,
        inLanguage: htmlLangMap[lang] || 'en',
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
      }
    }, {
      '@type': 'CommunicateAction',
      name: 'Telegram',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: TELEGRAM_URL,
        inLanguage: htmlLangMap[lang] || 'en',
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
      }
    }]
  };
  return <div className="min-h-screen flex flex-col bg-ivory" data-id="element-477" data-mp="eb5dshawi4h3">
      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} data-id="element-478" data-mp="120zpgvjhhmzr" />

      <Header data-id="element-479" data-mp="t8e5xd813c9v" />
      <main className="flex-grow" data-id="element-480" data-mp="t8xrr50sukt6">{children}</main>
      <Footer data-id="element-481" data-mp="t61rvb5e5m23" />

      {/* Sticky Mobile Booking Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-ivory/95 backdrop-blur-sm border-t border-beige/60 shadow-[0_-4px_16px_rgba(47,74,63,0.08)] px-3 py-2.5 safe-area-bottom" data-id="element-482" data-mp="x8odgubgyaqu">
        <div className="flex gap-2 items-stretch" data-id="element-483" data-mp="1pqakrxi2ep6i">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="flex-1" data-id="element-484" data-mp="1prvczggwpw17">
            <button className="w-full h-full flex items-center justify-center gap-2 bg-forest-600 text-white border border-forest-600 hover:bg-[#3A5A4D] font-medium text-sm py-2.5 px-3 rounded-xl transition-colors" data-id="element-485" data-mp="35gbgqhttxt9m">
              <Calendar className="w-4 h-4" data-id="element-486" data-mp="3f7o52zgf61aw" />
              {lang === 'en' ? 'Book Online' : lang === 'ua' ? 'Записатись' : lang === 'ro' ? 'Programare' : 'Записаться'}
            </button>
          </a>
          <a href="tel:+447345197820" className="flex-shrink-0" data-id="element-487" data-mp="31h2nde2pbxel">
            <button aria-label="Call clinic" className="h-full flex items-center justify-center bg-transparent text-forest-600 border border-forest-600 hover:bg-forest-600 hover:text-white py-2.5 px-3.5 rounded-xl transition-colors" data-id="element-488" data-mp="2wfmytc1bf8rg">
              <Phone className="w-5 h-5" strokeWidth={1.75} data-id="element-489" data-mp="2hezp0w5fhmse" />
            </button>
          </a>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-id="element-490" data-mp="2mdj8mpwjumhl">
            <button aria-label="WhatsApp" className="h-full flex items-center justify-center bg-forest-600 text-white border border-forest-600 hover:bg-[#3A5A4D] py-2.5 px-3.5 rounded-xl transition-colors" data-id="element-491" data-mp="2qe39a3gc088h">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} data-id="element-492" data-mp="35c5esj6xb3sd" />
            </button>
          </a>
          <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-id="element-493" data-mp="3a3571b53kxoj">
            <button aria-label="Telegram" className="h-full flex items-center justify-center bg-ivory-200 text-forest-600 border border-beige hover:bg-forest-600 hover:text-white py-2.5 px-3.5 rounded-xl transition-colors" data-id="element-494" data-mp="3e6tezuxn5vab">
              <TelegramIcon className="w-5 h-5" data-id="element-495" data-mp="23ab1nyqjof1" />
            </button>
          </a>
        </div>
      </div>

      <style data-id="element-496" data-mp="3t6p5z8hquu6u">{`
        .safe-area-bottom {
          padding-bottom: max(0.625rem, env(safe-area-inset-bottom));
        }
        @media (max-width: 767px) {
          main { padding-bottom: 72px; }
        }
      `}</style>
    </div>;
}